import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, Copy, BookOpen, Users, Search, ArrowRight } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { useBranding } from "@/contexts/BrandingContext";
import { CourseDialog, AWARDS } from "@/components/t4r/CourseDialogs";

const emptyCourse = { title: "", award: AWARDS[0], summary: "", is_published: false };

const CourseCard = ({ c, onOpen, onDuplicate }) => (
  <div onClick={onOpen} className="group cursor-pointer rounded-xl border border-border bg-white p-5 transition-colors hover:border-green-400" data-testid={`course-row-${c.id}`}>
    <div className="flex items-start justify-between gap-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-green-700">{c.award || "Course"}</p>
      <Badge variant="outline" className={`text-[10px] ${c.is_published ? "bg-green-100 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{c.is_published ? "Published" : "Draft"}</Badge>
    </div>
    <h3 className="mt-1 font-heading text-lg font-semibold leading-snug text-slate-900">{c.title}</h3>
    {c.summary && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{c.summary}</p>}
    <div className="mt-4 flex items-center gap-4 text-xs text-slate-600">
      <span className="inline-flex items-center gap-1"><BookOpen size={12} /> {c.lesson_count} lesson{c.lesson_count === 1 ? "" : "s"}</span>
      <span className="inline-flex items-center gap-1"><Users size={12} /> {c.learner_count} learner{c.learner_count === 1 ? "" : "s"}</span>
      <button onClick={(e) => { e.stopPropagation(); onDuplicate(); }} className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-1 text-slate-500 hover:bg-slate-100" title="Duplicate" data-testid={`course-duplicate-${c.id}`}><Copy size={12} /> Duplicate</button>
      <span className="inline-flex items-center gap-1 font-medium text-green-700 group-hover:translate-x-0.5 transition-transform" data-testid={`course-open-${c.id}`}>Open <ArrowRight size={12} /></span>
    </div>
  </div>
);

export default function T4RCoursesPage() {
  const { lms_name } = useBranding();
  useDocumentTitle("Courses", "Courses, lessons and materials", lms_name);
  const navigate = useNavigate();
  const [courses, setCourses] = useState(null);
  const [form, setForm] = useState(emptyCourse);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => { const { data } = await api.get("/lms/admin/courses"); setCourses(data.courses || []); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Give the course a title"); return; }
    try { const { data } = await api.post("/lms/admin/courses", form); toast.success("Course created as a draft"); setOpen(false); navigate(`/t4r/courses/${data.id}?tab=lessons`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const duplicate = async (c) => {
    try { const { data } = await api.post(`/lms/admin/courses/${c.id}/duplicate`); toast.success(`Created "${data.title}" as a draft`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't duplicate"); }
  };
  const shown = useMemo(() => (courses || []).filter((c) => (!q || c.title.toLowerCase().includes(q.toLowerCase())) && (!status || (status === "published") === !!c.is_published)), [courses, q, status]);

  return (
    <div className="space-y-5" data-testid="t4r-courses-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-heading text-2xl font-bold tracking-tight">Courses</h1>
        <Button size="sm" className="gap-1 bg-green-700 text-white hover:bg-green-800" onClick={() => { setForm(emptyCourse); setOpen(true); }} data-testid="new-course-btn"><Plus size={12} /> New course</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search courses" className="pl-8" data-testid="course-search" /></div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border border-border bg-white px-2 text-sm" data-testid="course-filter-status">
          <option value="">All</option><option value="published">Published</option><option value="draft">Drafts</option>
        </select>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {shown.map((c) => <CourseCard key={c.id} c={c} onOpen={() => navigate(`/t4r/courses/${c.id}`)} onDuplicate={() => duplicate(c)} />)}
        {courses && shown.length === 0 && <p className="text-sm italic text-muted-foreground">{courses.length ? "No courses match." : "No courses yet — create your first one."}</p>}
      </div>
      <CourseDialog open={open} onOpenChange={setOpen} editing={null} form={form} setForm={setForm} onSave={create} />
    </div>
  );
}
